/*
 * The rendered markdown tree, hand-written (protocol.md §5.2, spec 06 §3.1). The server parses
 * markdown with `internal/core/markdown` and ships a typed tree (`core.Render` in core.gen.ts); tygo
 * emits `string` for a Go const block, so the discriminated union that makes the renderer exhaustive
 * lives here rather than in the generated file. Every module that shows markdown — a chat message, an
 * AI chat's prose — draws this one tree.
 *
 * Every string field carries literal characters, never markup: nothing in this tree is ever
 * interpreted as HTML, and the only thing that turns a node into an element is the closed registry
 * of `MarkdownBody.tsx` (REUSE G1).
 */

export type NodeType =
	| "paragraph"
	| "heading"
	| "quote"
	| "list"
	| "item"
	| "code"
	| "table"
	| "row"
	| "cell"
	| "rule"
	| "text"
	| "em"
	| "strong"
	| "strike"
	| "codespan"
	| "link"
	| "break"
	| "permalink"
	| "image"
	| "math"
	| "mention"
	| "emoji"

/**
 * `[startByte, endByte]` into the message's markdown, on block nodes only. Selection copy maps a
 * DOM range onto these (A28), which is why a block renders them as `data-md`.
 */
export type Src = readonly number[]

/**
 * An internal link to one message. The parser sees only the URL, so it fills `kind` and `ref` — the
 * route segments the client already knows how to navigate — and leaves `conversationId` 0 for a
 * caller that has the database to fill in. Nothing does, so the address the author wrote is the only
 * one there is, and it is what the chip is drawn from.
 */
export interface Permalink {
	conversationId: number
	/** "c" for a channel-style address, "d" for a direct conversation's handle. */
	kind: string
	/** The slug or the direct handle exactly as it stood in the link; the address itself. */
	ref: string
	seq: number
}

/**
 * A typed mention. The body always stores the token verbatim ("@anna", "#allgemein"); `id` is filled
 * in by the server once the handle resolved to an account or to a mentionable group, and stays 0 — an
 * unresolved token, rendered as plain text — when it resolved to neither.
 *
 * Every other kind is a token a module registered with the server's parser (`core.MentionTokenDef`:
 * chat's `channel` and `conversation`). It carries no `id`, and the module that registered it draws
 * it (`RenderContextValue.mentionKinds`); a kind nobody draws is the characters the author typed.
 */
export interface Mention {
	kind: MentionKind
	handle: string
	id?: number
}

/** The two kinds core resolves an `@handle` to, and every kind a module registered beside them. */
export type MentionKind = "user" | "group" | (string & {})

/** Emitted for a literal Unicode emoji and for a resolved `:shortcode:` alike. */
export interface EmojiRef {
	char: string
	shortcode?: string
	name?: string
	custom?: string
}

export interface ParagraphNode {
	type: "paragraph"
	children?: MarkdownNode[]
	src?: Src
}
export interface HeadingNode {
	type: "heading"
	level: number
	children?: MarkdownNode[]
	src?: Src
}
export interface QuoteNode {
	type: "quote"
	children?: MarkdownNode[]
	src?: Src
}
export interface ListNode {
	type: "list"
	ordered?: boolean
	start?: number
	tight?: boolean
	children?: MarkdownNode[]
	src?: Src
}
export interface ItemNode {
	type: "item"
	/** Absent means "not a task item"; present means a checkbox in that state. */
	checked?: boolean
	children?: MarkdownNode[]
}
export interface CodeNode {
	type: "code"
	text: string
	lang?: string
	src?: Src
}
export interface TableNode {
	type: "table"
	/** One entry per column: "", "left", "center" or "right". */
	align?: string[]
	children?: MarkdownNode[]
	src?: Src
}
export interface RowNode {
	type: "row"
	head?: boolean
	children?: MarkdownNode[]
}
export interface CellNode {
	type: "cell"
	children?: MarkdownNode[]
}
export interface RuleNode {
	type: "rule"
	src?: Src
}
export interface TextNode {
	type: "text"
	text: string
}
export interface EmNode {
	type: "em"
	children?: MarkdownNode[]
}
export interface StrongNode {
	type: "strong"
	children?: MarkdownNode[]
}
export interface StrikeNode {
	type: "strike"
	children?: MarkdownNode[]
}
export interface CodeSpanNode {
	type: "codespan"
	text: string
}
export interface LinkNode {
	type: "link"
	href: string
	title?: string
	/** The link came from an autolink literal, so its text is the URL itself. */
	auto?: boolean
	/** Set when the link *text* is a URL of another host — the phishing tell, shown beside it. */
	hrefHost?: string
	children?: MarkdownNode[]
}
export interface BreakNode {
	type: "break"
}
export interface PermalinkNode {
	type: "permalink"
	perma: Permalink
}
export interface ImageNode {
	type: "image"
	href: string
	alt?: string
	title?: string
}
/** Reserved: the server never emits it in v1, and the renderer shows the source as text. */
export interface MathNode {
	type: "math"
	latex: string
	inline?: boolean
}
export interface MentionNode {
	type: "mention"
	mention: Mention
}
export interface EmojiNode {
	type: "emoji"
	emoji: EmojiRef
}

export type MarkdownNode =
	| ParagraphNode
	| HeadingNode
	| QuoteNode
	| ListNode
	| ItemNode
	| CodeNode
	| TableNode
	| RowNode
	| CellNode
	| RuleNode
	| TextNode
	| EmNode
	| StrongNode
	| StrikeNode
	| CodeSpanNode
	| LinkNode
	| BreakNode
	| PermalinkNode
	| ImageNode
	| MathNode
	| MentionNode
	| EmojiNode

/** What ships with a body: the tree, the version that produced it, and its flag bitset. */
export interface Render {
	ver: number
	flags: number
	nodes: MarkdownNode[]
}

/**
 * The bits of `Render.flags` (spec 06 §2, `internal/core/wire/markdown.go`). Only `jumbo` and `truncated`
 * change what is drawn; the rest let a surface decide cheaply whether a walk is worth it.
 */
export const RenderFlag = {
	jumbo: 1,
	hasCode: 2,
	hasTable: 4,
	hasMath: 8,
	truncated: 16,
	hasMention: 32,
	hasLink: 64,
} as const
